import React, { useEffect, useRef, useState } from "react";
import axiosInstance from "../axiosinstance";
import { IoIosSearch } from "react-icons/io";
import { LuPlus } from "react-icons/lu";
import { CiEdit } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import { PiSortAscendingThin } from "react-icons/pi";
import "../Tables/Table.css";
import Pagination from "../Components/Pagination";

const FamilyGroup = ({ isSidebarExpanded }) => {
  const [familyGroups, setFamilyGroups] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isSortPopup, setIsSortPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });

  const navigate = useNavigate();
  const containerRef = useRef();

  const fetchFamilyGroups = async (
    currentPage = 1,
    perPage = itemsPerPage,
    query = "",
  ) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get("/api/get/family_group", {
        params: {
          page: Math.max(1, currentPage),
          per_page: perPage,
          search: query || "",
        },
      });
      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      setFamilyGroups(data);
      setTotalRecords(
        response.data?.total_count ??
          response.data?.Total_records ??
          response.data?.totalRecords ??
          data.length,
      );
    } catch (error) {
      console.error("Error fetching family groups:", error);
      setFamilyGroups([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (
    query,
    currentPage = 1,
    perPage = itemsPerPage,
  ) => fetchFamilyGroups(currentPage, perPage, query);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    if (debouncedSearch.trim()) {
      handleSearch(debouncedSearch, page, itemsPerPage);
    } else {
      fetchFamilyGroups(page, itemsPerPage, "");
    }
  }, [debouncedSearch, page, itemsPerPage]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    const sorted = [...familyGroups].sort((a, b) => {
      const aVal = (a[key] || "").toString().toLowerCase();
      const bVal = (b[key] || "").toString().toLowerCase();

      if (aVal < bVal) return direction === "asc" ? -1 : 1;
      if (aVal > bVal) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setSortConfig({ key, direction });
    setFamilyGroups(sorted);
    setIsSortPopup(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsSortPopup(false);
      }
    };

    if (isSortPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSortPopup]);

  const handlePaginationChange = (newPage, perPage) => {
    setPage(newPage);
    setItemsPerPage(perPage);
  };

  const toTitleCase = (str) =>
    str
      ? str
          .toLowerCase()
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
      : "NA";

  return (
    <div
      className={`table-container family-groups-page ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="table-container-content family-groups-content">
        <div className="table-header">
          <div className="table-header-content">
            <div className="table-left-side">
              <h2 className="jost-bold">Family Groups</h2>
            </div>
            <div className="table-right-side">
              <div className="search-bar-container">
                <input
                  type="text"
                  placeholder="Search ..."
                  className="search-bar"
                  value={searchQuery}
                  onChange={(event) => {
                    setSearchQuery(event.target.value);
                    setPage(1);
                  }}
                />
                <IoIosSearch className="search-bar-icon" />
              </div>

              <div
                className="new-button"
                onClick={() => navigate("/family-group-form")}
              >
                <span className="new-icon">
                  <LuPlus />
                </span>
                <span>New</span>
              </div>

              <button
                className="family-groups-sort-button"
                onClick={(event) => {
                  event.stopPropagation();
                  setIsSortPopup(!isSortPopup);
                }}
              >
                <span className="action-icon custom-action-icon-size">
                  <PiSortAscendingThin />
                </span>
              </button>

              {isSortPopup && (
                <div className="sort-popup-container" ref={containerRef}>
                  <div className="sort-popup-header">
                    <span className="sort-title">Sort By</span>
                  </div>
                  <div className="sort-popup-content">
                    <button
                      className="sort-button"
                      onClick={() => handleSort("family_name")}
                    >
                      Family Name
                    </button>
                    <button
                      className="sort-button"
                      onClick={() => handleSort("contact_name")}
                    >
                      Contact Name
                    </button>
                    <button
                      className="sort-button"
                      onClick={() => handleSort("email")}
                    >
                      Email
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="data-list-body ledger-data-list-body family-groups-data-body pb0">
          <div
            className="data-list-scroll-body ledger-scroll-body family-groups-scroll-body"
            style={{ padding: "0px" }}
          >
            <table className="table multilevel-data family-groups-table">
              <thead className="sticky-header">
                <tr className="table-row">
                  <th>Sr. No</th>
                  <th>
                    <span
                      style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                      }}
                    >
                      Family Name
                    </span>
                  </th>
                  <th>
                    <span
                      style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                      }}
                    >
                      Contact Name
                    </span>
                  </th>
                  <th>
                    <span
                      style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                      }}
                    >
                      Phone Number
                    </span>
                  </th>
                  <th>
                    <span
                      style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                      }}
                    >
                      Email
                    </span>
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="6" className="center-align">
                      Loading Family Groups...
                    </td>
                  </tr>
                ) : familyGroups.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="center-align">
                      No Family Groups Found
                    </td>
                  </tr>
                ) : (
                  familyGroups.map((fg, index) => (
                    <tr key={fg.family_id} className="hoverable-row">
                      <td className="center-align">
                        {(page - 1) * itemsPerPage + index + 1}
                      </td>
                      <td className="family-name-cell">
                        {toTitleCase(fg.family_name)}
                      </td>
                      <td className="family-contact-cell">
                        {toTitleCase(fg.contact_name)}
                      </td>
                      <td className="family-phone-cell">
                        {fg.phone_number || "NA"}
                      </td>
                      <td className="family-email-cell" title={fg.email || "NA"}>
                        {fg.email || "NA"}
                      </td>
                      <td className="center-align">
                        <div className="action-btn">
                          <span
                            className="action-icon"
                            onClick={() =>
                              navigate(`/edit-family-group/${fg.family_id}`)
                            }
                          >
                            <CiEdit className="icn" />
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            total={totalRecords}
            showPerPage={itemsPerPage}
            onPaginationChange={handlePaginationChange}
            className="family-groups-pagination"
          />
        </div>
      </div>
    </div>
  );
};

export default FamilyGroup;
